var TimestampTrackerState;
(function (TimestampTrackerState) {
    TimestampTrackerState[TimestampTrackerState["Init"] = 0] = "Init";
    TimestampTrackerState[TimestampTrackerState["Created"] = 1] = "Created";
    TimestampTrackerState[TimestampTrackerState["Active"] = 2] = "Active";
    TimestampTrackerState[TimestampTrackerState["Paused"] = 3] = "Paused";
    TimestampTrackerState[TimestampTrackerState["Unpaused"] = 4] = "Unpaused";
})(TimestampTrackerState || (TimestampTrackerState = {}));
class TimestampTracker {
    constructor() {
        this.state = TimestampTrackerState.Init;
        this.firstTimestamp = 0;
        this.lastTimestamp = 0;
        this.pausedTimestamp = 0;
        this.tsDelta = 0;
        this.tsFixup = 0;
    }
    convertTimestamp(ts) {
        if (this.state === TimestampTrackerState.Init) {
            this.state = TimestampTrackerState.Created;
            this.firstTimestamp = ts;
            return 0;
        }
        if (ts < this.firstTimestamp) {
            console.warn("TimestampTracker: timestamp overflow", ts, this.firstTimestamp);
            this.firstTimestamp = this.firstTimestamp - Math.pow(2, 32);
        }
        ts = ts - this.firstTimestamp;
        switch (this.state) {
            case TimestampTrackerState.Created:
                this.state = TimestampTrackerState.Active;
                this.lastTimestamp = this.tsDelta = ts;
                return ts;
            case TimestampTrackerState.Active:
                this.lastTimestamp = ts;
                return ts - this.tsFixup;
            case TimestampTrackerState.Paused:
                this.lastTimestamp = ts;
                return ts - this.tsFixup;
            case TimestampTrackerState.Unpaused:
                this.state = TimestampTrackerState.Active;
                this.tsFixup += ts - this.pausedTimestamp - this.tsDelta;
                this.lastTimestamp = ts;
                return ts - this.tsFixup;
        }
    }
    pause() {
        if (this.state !== TimestampTrackerState.Active)
            throw Error(`Invalid state for pausing ${this.state}`);
        this.state = TimestampTrackerState.Paused;
        this.pausedTimestamp = this.lastTimestamp;
    }
    resume() {
        if (!(this.state === TimestampTrackerState.Paused || this.state === TimestampTrackerState.Unpaused))
            throw Error(`Invalid state for resuming ${this.state}`);
        this.state = TimestampTrackerState.Unpaused;
    }
}
class Uint8ArrayEvent {
    constructor(data) {
        this.data = data;
    }
}
class LocalWebRTCPipe {
    constructor(options) {
        this.audioTimestampTracker = new TimestampTracker();
        const senderConstraints = {
            optional: [{ googCpuOveruseDetection: false }],
            mandatory: { googCpuOveruseDetection: false }
        };
        let RTCPeerConnection2 = RTCPeerConnection;
        this.pc1 = new RTCPeerConnection2({ encodedInsertableStreams: true }, senderConstraints);
        this.pc2 = new RTCPeerConnection({ encodedInsertableStreams: false });
        this.pc1.addTrack(options.audioTrack);
        this.modifyCodecs();
        this.pc1.onicecandidate = e => {
            if (!e.candidate)
                return;
            this.pc2.addIceCandidate(e.candidate);
        };
        this.pc2.onicecandidate = e => {
            if (!e.candidate)
                return;
            this.pc1.addIceCandidate(e.candidate);
        };
        this.pc1.getSenders().forEach((sender) => {
            let senderStreams = sender.createEncodedStreams();
            const transformStream = new TransformStream({
                transform: (encodedFrame, controller) => {
                    if (this.audioTimestampTracker.state !== TimestampTrackerState.Paused) {
                        const ts = this.audioTimestampTracker.convertTimestamp(encodedFrame.timestamp);
                        options.encodedFrameCb(encodedFrame, ts, performance.now());
                    }
                    controller.enqueue(encodedFrame);
                },
                flush: () => {
                }
            });
            senderStreams.readable
                .pipeThrough(transformStream)
                .pipeTo(senderStreams.writable);
        });
    }
    get description() {
        return this.pc1.currentLocalDescription.sdp
            .split("\r\n")
            .filter(l => /^v=0|^m=|^a=ssrc.*cname/.test(l))
            .join('\n');
    }
    processSDP(sdp_init) {
        let { type, sdp } = sdp_init;
        let arr = sdp.split('\r\n').filter(str => {
            return !(/a=extmap/.test(str));
        });
        arr.forEach((str, i) => {
            if (/^a=mid:(0|audio)/.test(str)) {
                arr[i] += '\r\nb=AS:600';
            }
            else if (/useinbandfec=1/.test(str)) {
                arr[i] = str.replace('useinbandfec=1', 'useinbandfec=0') + "\r\na=ptime:120";
            }
        });
        return { type, sdp: arr.join('\r\n') };
    }
    modifyCodecs() {
        const opusCodecs = RTCRtpSender.getCapabilities('audio').codecs.filter(c => c.mimeType === "audio/opus");
        this.pc1.getTransceivers().forEach(t => {
            if (t.sender.dtmf !== null) {
                t.setCodecPreferences(opusCodecs);
            }
        });
    }
    async negotiate() {
        const offer = this.processSDP(await this.pc1.createOffer());
        await this.pc2.setRemoteDescription({ type: 'offer', sdp: offer.sdp.replace('red/90000', 'green/90000') });
        await this.pc1.setLocalDescription(offer);
        this.answer = this.processSDP(await this.pc2.createAnswer());
        await this.pc2.setLocalDescription(this.answer);
    }
    start() {
        this.pc1.setRemoteDescription(this.answer);
    }
    stop() {
        this.pc1.close();
        this.pc2.close();
    }
    pause() {
        this.audioTimestampTracker.pause();
    }
    resume() {
        this.audioTimestampTracker.resume();
    }
    getSafeAudioTrack() {
        const audioTrack = this.pc2.getReceivers()
            .map(r => r.track)
            .filter(t => t.kind === "audio")[0];
        if (audioTrack) {
            return Promise.resolve(audioTrack);
        }
        return new Promise((resolve) => {
            const listener = (e) => {
                if (e.track.kind === "audio") {
                    this.pc2.removeEventListener("track", listener);
                    resolve(e.track);
                }
            };
            this.pc2.addEventListener("track", listener);
        });
    }
}
class WebCodecsVideoRecorder {
    constructor(options) {
        this.paused = false;
        this.forceKeyframe = false;
        this.timestampTracker = new TimestampTracker();
        this.timestampMap = new Map();
        this.framesInQueue = 0;
        this.videoTrack = options.videoTrack;
        const { width, height } = this.videoTrack.getSettings();
        this.videoTrackReader = new VideoTrackReader(this.videoTrack);
        const createAndConfigureEncoder = (hardware) => {
            this.videoEncoder = new VideoEncoder({
                error: (e) => {
                    if (hardware) {
                        console.warn("Hardware encoder is not available, reconfiguring to software");
                        createAndConfigureEncoder(false);
                    }
                    else {
                        options.errorCb(e);
                    }
                },
                output: (encodedFrame) => {
                    this.framesInQueue--;
                    const rawTs = encodedFrame.timestamp;
                    const [ts, now] = this.timestampMap.get(rawTs);
                    this.timestampMap.delete(rawTs);
                    options.encodedFrameCb(encodedFrame, ts, now);
                }
            });
            const t = this.videoEncoder.configure({
                codec: hardware ? "avc1.424028" : "avc1.424028",
                acceleration: hardware ? "require" : "allow",
                bitrate: 3500000,
                framerate: 30,
                width,
                height
            });
        };
        createAndConfigureEncoder(true);
    }
    static checkVideoEncoder(params) {
        return new Promise((resolve, reject) => {
            let result = "HW";
            const timeoutHandle = setTimeout(() => {
                resolve(result);
            }, 1000);
            const createAndConfigureEncoder = (hardware) => {
                const _ = new VideoEncoder({
                    error: (e) => {
                        if (hardware) {
                            console.warn("Hardware encoder is not available, trying software");
                            result = "SW";
                            createAndConfigureEncoder(false);
                        }
                        else {
                            clearTimeout(timeoutHandle);
                            reject();
                        }
                    },
                    output: () => console.log("Not Used")
                });
                _.configure({
                    codec: hardware ? "avc1.424028" : "avc1.424028",
                    acceleration: hardware ? "require" : "allow",
                    bitrate: 3500000,
                    framerate: 30,
                    width: 1280,
                    height: 720
                });
            };
            createAndConfigureEncoder(true);
        });
    }
    start() {
        this.videoTrackReader.start(frame => {
            if (this.paused) {
                frame.destroy();
                return;
            }
            this.framesInQueue++;
            const rawTs = frame.timestamp;
            this.timestampMap.set(rawTs, [this.timestampTracker.convertTimestamp(rawTs), performance.now()]);
            this.videoEncoder.encode(frame, this.forceKeyframe ? { keyFrame: true } : undefined);
            this.forceKeyframe = false;
        });
    }
    stop() {
        this.videoTrackReader.stop();
        this.videoEncoder.flush();
        this.videoEncoder.close();
        console.log("Frames in queue after stop", this.framesInQueue);
    }
    pause() {
        this.paused = true;
        this.timestampTracker.pause();
    }
    resume() {
        this.forceKeyframe = true;
        this.paused = false;
        this.timestampTracker.resume();
    }
}
class InsertableStreamRecorderBase {
    constructor() {
        this.CACHE_SZIE = 2 * 1024 * 1024;
        this.bufferCache = null;
        this.bufferCacheUsed = 0;
        this.frameCount = 0;
        this.state = 'inactive';
        this.ondataavailable = null;
        this.onstop = null;
        this.tsDebugDataHeader = InsertableStreamRecorderBase.textEncoder.encode("now(Float64),rawTs(Float64),ts(Float64),frameType(char)\n").buffer;
        this._tsDebugData = [this.tsDebugDataHeader];
    }
    get tsDebugData() {
        return new Blob(this._tsDebugData, { type: "application/octet-stream" });
    }
    addDebugInfo(frameType, ts, rawTs, now) {
        const line = new ArrayBuffer(25);
        const view = new DataView(line);
        view.setFloat64(0, now);
        view.setFloat64(8, rawTs);
        view.setFloat64(16, ts);
        view.setUint8(24, frameType.charCodeAt(0));
        this._tsDebugData.push(line);
    }
    sendToProcessing(data) {
        var _a;
        (_a = this.ondataavailable) === null || _a === void 0 ? void 0 : _a.call(this, new Uint8ArrayEvent(data));
    }
    processData(...args) {
        var _a;
        if (args.length === 0) {
            if (this.bufferCache && this.bufferCacheUsed) {
                this.sendToProcessing(new Uint8Array(this.bufferCache.buffer, 0, this.bufferCacheUsed));
                this.bufferCache = null;
                this.bufferCacheUsed = 0;
                (_a = this.onstop) === null || _a === void 0 ? void 0 : _a.call(this);
            }
            return;
        }
        const sizeRequired = args.reduce((sum, elem) => sum + elem.length, 0);
        if (!this.bufferCache || (this.bufferCache.length - this.bufferCacheUsed) < sizeRequired) {
            if (this.bufferCache) {
                this.sendToProcessing(new Uint8Array(this.bufferCache.buffer, 0, this.bufferCacheUsed));
            }
            this.bufferCache = new Uint8Array(Math.max(this.CACHE_SZIE, sizeRequired));
            this.bufferCacheUsed = 0;
        }
        args.forEach((data) => {
            this.bufferCache.set(data, this.bufferCacheUsed);
            this.bufferCacheUsed += data.length;
        });
        if (this.bufferCache.length === this.bufferCacheUsed) {
            this.sendToProcessing(this.bufferCache);
            this.bufferCache = null;
            this.bufferCacheUsed = 0;
        }
    }
    generateHeaderIfNeeded() {
        if (!this.frameCount) {
            this.processData(InsertableStreamRecorderBase.textEncoder.encode(this.headerDescription() + "\0"));
        }
        this.frameCount++;
    }
    processEncodedFrame(encodedFrame, frameType, ssrc, ts, now) {
        this.generateHeaderIfNeeded();
        const size = encodedFrame.data.byteLength;
        const rawTs = encodedFrame.timestamp;
        const frameHeaderText = `${frameType} ${size} ${ts} ${ssrc} ${now}\0`;
        const frameHeader = InsertableStreamRecorderBase.textEncoder.encode(frameHeaderText);
        this.processData(frameHeader, new Uint8Array(encodedFrame.data));
        this.addDebugInfo(frameType, ts, rawTs, now);
    }
}
InsertableStreamRecorderBase.textEncoder = new TextEncoder();
class InsertableStreamAudioRecorder extends InsertableStreamRecorderBase {
    constructor(options) {
        super();
        const audioTrack = options.mediaStream.getAudioTracks()[0];
        if (!(audioTrack instanceof MediaStreamTrack) || audioTrack.readyState !== 'live') {
            throw new Error('audioTrack must be an active media stream track');
        }
        this.audioRecorder = new LocalWebRTCPipe({
            audioTrack,
            encodedFrameCb: this.encodedAudioFrame.bind(this)
        });
    }
    headerDescription() {
        return this.audioRecorder.description;
    }
    encodedAudioFrame(encodedFrame, ts, now) {
        const frameType = 'audio';
        const ssrc = encodedFrame.getMetadata().synchronizationSource;
        this.processEncodedFrame(encodedFrame, frameType, ssrc, ts, now);
    }
    async start() {
        await this.audioRecorder.negotiate();
        this.state = 'recording';
        this.audioRecorder.start();
    }
    stop() {
        this.audioRecorder.stop();
        this.processData();
        this.state = 'inactive';
    }
    pause() {
        console.warn("Recording paused");
        this.state = 'paused';
        this.audioRecorder.pause();
    }
    resume() {
        console.warn("Recording unpaused");
        this.state = 'recording';
        this.audioRecorder.resume();
    }
    getSafeAudioTrack() {
        return this.audioRecorder.getSafeAudioTrack();
    }
}
class InsertableStreamRecorder extends InsertableStreamAudioRecorder {
    constructor(options) {
        super(options);
        const videoTrack = options.mediaStream.getVideoTracks()[0];
        if (!videoTrack) {
            throw new Error('videoTrack must is missing');
        }
        this.videoRecorder = new WebCodecsVideoRecorder({
            videoTrack,
            errorCb: (e) => console.error(e),
            encodedFrameCb: this.encodedVideoFrame.bind(this)
        });
    }
    headerDescription() {
        return super.headerDescription() + "\nm=video\na=ssrc:1\n";
    }
    encodedVideoFrame(encodedFrame, ts, now) {
        const frameType = encodedFrame.type;
        const ssrc = 1;
        this.processEncodedFrame(encodedFrame, frameType, ssrc, ts, now);
    }
    async start() {
        await super.start();
        this.videoRecorder.start();
    }
    stop() {
        this.videoRecorder.stop();
        super.stop();
    }
    pause() {
        super.pause();
        this.videoRecorder.pause();
    }
    resume() {
        super.resume();
        this.videoRecorder.resume();
    }
}
//# sourceMappingURL=insertable_stream_recorder.js.map